import * as Sentry from '@sentry/vue';
import { GLTFDefaultDataSource } from '@xeokit/xeokit-sdk/src/plugins/GLTFLoaderPlugin/GLTFDefaultDataSource.js';
import { IGLTFDefaultDataSource } from '@xeokit/xeokit-sdk/src/plugins/GLTFLoaderPlugin/GLTFLoaderPlugin.js';
import { core } from '@xeokit/xeokit-sdk/src/viewer/scene/core.js';

export class DataSourceGLTF extends GLTFDefaultDataSource implements IGLTFDefaultDataSource {
  cacheBuster: boolean = false;

  constructor(cfg: any = {}) {
    super(cfg);
    this.cacheBuster = cfg.cacheBuster !== false;
  }

  _cacheBusterURL(url: string) {
    if (!this.cacheBuster) {
      return url;
    }
    const timestamp = new Date().getTime();
    if (url.indexOf('?') > -1) {
      return url + '&_=' + timestamp;
    } else {
      return url + '?_=' + timestamp;
    }
  }

  getMetaModel(metaModelSrc: string, ok: (data: any) => void, err: (e: Error) => void) {
    this.loadJSON(
      this._cacheBusterURL(metaModelSrc),
      (json) => {
        ok(json);
      },
      function (errMsg) {
        err(errMsg);
      },
    );
  }

  getArrayBuffer(
    glTFSrc: string,
    binarySrc: string,
    ok: (data: any) => void,
    err: (e: Error) => void,
  ) {
    this.loadArraybuffer(
      this._cacheBusterURL(binarySrc),
      (arrayBuffer) => {
        ok(arrayBuffer);
      },
      function (errMsg) {
        err(errMsg);
      },
    );
  }

  getGLTF(src: string, ok: (data: any) => void, err: (e: Error) => void) {
    this.loadArraybuffer(
      this._cacheBusterURL(src),
      (gltf) => {
        ok(gltf);
      },
      function (errMsg) {
        err(errMsg);
      },
    );
  }

  getGLB(src: string, ok: (data: any) => void, err: (e: Error) => void) {
    this.loadArraybuffer(
      this._cacheBusterURL(src),
      (arraybuffer) => {
        ok(arraybuffer);
      },
      function (errMsg) {
        err(errMsg);
      },
    );
  }

  loadArraybuffer(src: string, ok: (data: any) => void, err: (e: Error) => void) {
    // Check for data: URI
    const defaultCallback = () => {};
    ok = ok || defaultCallback;
    err = err || defaultCallback;

    const processesStore = useProcessesStore();
    const config = useRuntimeConfig();

    const dataUriRegex = /^data:(.*?)(;base64)?,(.*)$/;
    const dataUriRegexResult = src.match(dataUriRegex);

    if (dataUriRegexResult) {
      const isBase64 = !!dataUriRegexResult[2];
      let data = dataUriRegexResult[3];
      data = window.decodeURIComponent(data);
      if (isBase64) {
        data = window.atob(data);
      }
      try {
        const buffer = new ArrayBuffer(data.length);
        const view = new Uint8Array(buffer);
        for (let i = 0; i < data.length; i++) {
          view[i] = data.charCodeAt(i);
        }
        core.scheduleTask(function () {
          ok(buffer);
        });
      } catch (error: any) {
        core.scheduleTask(function () {
          err(error as Error);
        });
        Sentry.captureException(error, {
          fingerprint: ['data-source-gltf', 'loadArraybuffer', src],
        });
      }
    } else {
      const request = new CachedXHR(config.public.URL_EXP_TIME * 1000);
      request.open('GET', src, true);

      request.onprogress = function (event: ProgressEvent) {
        if (event.lengthComputable) {
          processesStore.updateDownloadProgress(src as string, event.loaded, event.total);
        }
      };

      request.onabort = function (event: ProgressEvent) {
        const error = new Error('Download aborted');
        processesStore.updateDownloadProgress(src as string, event.loaded, event.total, error);
      };

      request.ontimeout = function (event: ProgressEvent) {
        const error = new Error('Download timeout');
        processesStore.updateDownloadProgress(src as string, event.loaded, event.total, error);
      };

      request.onreadystatechange = function () {
        if (request.readyState === 4) {
          if (request.status !== 200) {
            err(new Error('loadArrayBuffer error : ' + request.response));
          }
        }
      };

      request.addEventListener('finish', async (event: Event) => {
        const blob = (event as CustomEvent).detail as Blob;
        ok(await blob.arrayBuffer());
      });

      request.send(null);
    }
  }

  loadJSON(url: string, ok: (json: any) => void, err: (e: Error) => void) {
    // Avoid checking ok and err on each use.
    const defaultCallback = (_value) => undefined;
    ok = ok || defaultCallback;
    err = err || defaultCallback;

    const processesStore = useProcessesStore();
    const config = useRuntimeConfig();

    const request = new CachedXHR(config.public.URL_EXP_TIME * 1000);
    request.overrideMimeType('application/json');
    request.open('GET', url, true);

    request.onprogress = function (event: ProgressEvent) {
      if (event.lengthComputable) {
        processesStore.updateDownloadProgress(url as string, event.loaded, event.total);
      }
    };

    request.onabort = function (event: ProgressEvent) {
      const error = new Error('Download aborted');
      processesStore.updateDownloadProgress(url as string, event.loaded, event.total, error);
    };

    request.ontimeout = function (event: ProgressEvent) {
      const error = new Error('Download timeout');
      processesStore.updateDownloadProgress(url as string, event.loaded, event.total, error);
    };

    request.addEventListener('finish', async (event) => {
      try {
        const blob = (event as CustomEvent).detail as Blob;
        const json = JSON.parse(await blob.text());
        ok(json);
      } catch (error) {
        Sentry.captureException(error, {
          fingerprint: ['data-source-gltf', 'loadJSON', url],
        });

        err(new Error(`utils.loadJSON(): Failed to parse JSON response - ${error}`));
      }
    });

    request.addEventListener(
      'error',
      function (event) {
        err(new Error(`utils.loadJSON() Error: ${event}`));
      },
      false,
    );

    request.send(null);
  }
}
